import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import PostCard from '../posts/PostCard';

interface Post {
  id: string;
  userId: string;
  userName: string;
  userPhotoURL: string;
  content: string;
  imageUrl?: string;
  videoUrl?: string;
  likes: number;
  comments: number;
  createdAt: any;
  [key: string]: any;
}

interface DirectLikedPostsProps {
  userId: string;
}

const DirectLikedPosts: React.FC<DirectLikedPostsProps> = ({ userId }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLikedPosts = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Verificar primero si la colección de likes existe
        console.log(`[DirectLikedPosts] Verificando likes para el usuario ${userId}`);
        
        // Obtener directamente la colección de likes del usuario con ordenación
        const likesCollectionRef = collection(db, `users/${userId}/likes`);
        const likesQuery = query(likesCollectionRef, orderBy('createdAt', 'desc'), limit(50));
        const likesSnapshot = await getDocs(likesQuery);

        console.log(`[DirectLikedPosts] Encontrados ${likesSnapshot.size} likes para el usuario ${userId}`);

        if (likesSnapshot.empty) {
          console.log(`[DirectLikedPosts] No se encontraron likes para el usuario ${userId}`);
          setPosts([]);
          setLoading(false);
          return;
        }

        // Procesar cada documento de like de manera secuencial para evitar problemas
        const likedPosts: Post[] = [];
        
        // Iterar sobre cada documento de like
        for (const likeDoc of likesSnapshot.docs) {
          try {
            const postId = likeDoc.id;
            const likeData = likeDoc.data();
            
            console.log(`[DirectLikedPosts] Procesando like para el post ${postId}:`, likeData);
            
            // Intentar obtener el post original
            const postRef = doc(db, 'posts', postId);
            const postDoc = await getDoc(postRef);
            
            if (postDoc.exists()) {
              // El post existe, usar sus datos
              const postData = postDoc.data();
              likedPosts.push({
                id: postId,
                ...postData,
                userId: postData.userId || likeData.userId || '',
                userName: postData.userName || likeData.userName || 'Usuario',
                userPhotoURL: postData.userPhotoURL || likeData.userPhotoURL || '/default-avatar.png',
                content: postData.content || likeData.content || '',
                imageUrl: postData.imageUrl || likeData.imageUrl || '',
                videoUrl: postData.videoUrl || likeData.videoUrl || '',
                createdAt: postData.createdAt || likeData.postCreatedAt || new Date(),
                likedAt: likeData.createdAt || new Date(),
                likes: postData.likes || 0,
                comments: postData.comments || 0,
                reposts: postData.reposts || 0
              });
              console.log(`[DirectLikedPosts] Post ${postId} añadido desde datos originales`);
            } else {
              // El post no existe, usar datos del like
              likedPosts.push({
                id: postId,
                userId: likeData.userId || '',
                userName: likeData.userName || 'Usuario',
                userPhotoURL: likeData.userPhotoURL || '/default-avatar.png',
                content: likeData.content || 'Esta publicación ya no está disponible',
                imageUrl: likeData.imageUrl || '',
                videoUrl: likeData.videoUrl || '',
                createdAt: likeData.postCreatedAt || new Date(),
                likedAt: likeData.createdAt || new Date(),
                isDeleted: true,
                likes: 0,
                comments: 0,
                reposts: 0
              });
              console.log(`[DirectLikedPosts] Post ${postId} añadido desde datos del like (post eliminado)`);
            }
          } catch (error) {
            console.error(`[DirectLikedPosts] Error al procesar like:`, error);
          }
        }

        console.log(`[DirectLikedPosts] Procesados ${likedPosts.length} posts con "Me gusta"`);
        
        // Asegurarse de que los posts tengan todos los campos necesarios
        const validPosts = likedPosts.filter(post => 
          post && typeof post === 'object' && post.id && post.userId
        );
        
        console.log(`[DirectLikedPosts] ${validPosts.length} posts válidos para mostrar`);
        setPosts(validPosts);
      } catch (error) {
        console.error('[DirectLikedPosts] Error al cargar posts con "Me gusta":', error);
        setError('Error al cargar las publicaciones con "Me gusta".');
      } finally {
        setLoading(false);
      }
    };

    fetchLikedPosts();
  }, [userId]);

  if (loading) {
    return (
      <div className="text-center py-10 bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-domired-600 mx-auto mb-4"></div>
        <p className="text-gray-500 dark:text-gray-400">Cargando publicaciones con "Me gusta"...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10 bg-white dark:bg-gray-800 rounded-lg shadow">
        <p className="text-red-500 mb-4">{error}</p>
        <button 
          onClick={() => setLoading(true)}
          className="px-4 py-2 bg-domired-600 text-white rounded-md hover:bg-domired-700 transition-colors"
        >
          Intentar nuevamente
        </button>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-10 bg-white dark:bg-gray-800 rounded-lg shadow">
        <p className="text-gray-500 dark:text-gray-400">
          No hay publicaciones con "Me gusta".
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {posts.map(post => (
        <PostCard key={post.id} post={post} />
      ))}
    </div>
  );
};

export default DirectLikedPosts;
